// client/src/views/apps/user/utils/trend.js
//
// Pure helpers for the performance-graph trend lines (client's "trend" request).
// `linearTrend` fits a least-squares straight line; `overallSeries` averages the
// per-category series into an "overall" series. Both are framework-agnostic so
// they can be unit-tested without a chart or Vue.

// Coerce a value to a finite number, treating missing markers (null, undefined,
// empty string) as NaN. Guards against JS quirks like Number(null) === 0, so a
// "missing session" is skipped rather than silently counted as zero.
function coerce(value) {
  if (value === null || value === undefined || value === '') return NaN;
  return Number(value);
}

/**
 * Least-squares linear regression over a series indexed by position (0..n-1).
 * Returns a same-length array of fitted values (a straight trend line), or an
 * empty array when fewer than two finite points exist (a trend needs >= 2).
 * Non-finite entries are ignored for the fit but the line still spans every
 * index so it aligns with the original series on the chart.
 *
 * @param {Array<number|null|undefined>} values
 * @returns {number[]}
 */
export function linearTrend(values) {
  if (!Array.isArray(values)) return [];

  const points = [];
  values.forEach((value, index) => {
    const y = coerce(value);
    if (Number.isFinite(y)) points.push([index, y]);
  });

  if (points.length < 2) return [];

  const n = points.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (const [x, y] of points) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }

  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return [];

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  return values.map((_, index) => intercept + slope * index);
}

/**
 * Index-by-index unweighted mean across category series, skipping non-finite or
 * missing points so a shorter/sparse category does not break a session average.
 * Length follows the longest category. Returns [] when given no categories.
 *
 * @param {Array<Array<number|null|undefined>>} categorySeries
 * @returns {number[]}
 */
export function overallSeries(categorySeries) {
  if (!Array.isArray(categorySeries) || categorySeries.length === 0) return [];

  const length = Math.max(
    0,
    ...categorySeries.map((series) => (Array.isArray(series) ? series.length : 0))
  );

  const result = [];
  for (let i = 0; i < length; i++) {
    let sum = 0;
    let count = 0;
    for (const series of categorySeries) {
      const y = coerce(Array.isArray(series) ? series[i] : undefined);
      if (Number.isFinite(y)) {
        sum += y;
        count += 1;
      }
    }
    result.push(count ? sum / count : NaN);
  }
  return result;
}

/**
 * Assemble the performance chart's building blocks: the actual per-category
 * series, a dashed colour-matched trend line per category, and one "overall"
 * trend line (regression of the per-session category average). Returns plain
 * arrays aligned by series index so a chart layer can map them onto its own
 * options (colours, stroke widths, dash patterns, data-label gating).
 *
 * Pure and framework-agnostic — translated labels and theme colours are passed
 * in rather than resolved here.
 *
 * @param {Array<{name: string, data: Array<number>}>} categories
 * @param {Object} opts
 * @param {string[]} opts.baseColors  one colour per category (by index)
 * @param {string} opts.overallColor  colour for the overall trend line
 * @param {string} opts.trendLabel    translated word for "Trend"
 * @param {string} opts.overallLabel  translated word for "Overall"
 * @returns {{series: Array, colors: string[], width: number[], dashArray: number[], dataLabelSeries: number[]}}
 */
export function buildTrendChart(categories, opts = {}) {
  const {
    baseColors = [],
    overallColor = '#5e5873',
    trendLabel = 'Trend',
    overallLabel = 'Overall',
  } = opts;

  const list = Array.isArray(categories) ? categories : [];
  const series = [];
  const colors = [];
  const width = [];
  const dashArray = [];
  const dataLabelSeries = [];
  const categoryValues = [];

  // 1) Actual per-category lines — solid, with data labels.
  list.forEach((category, i) => {
    series.push({ name: category.name, data: category.data });
    colors.push(baseColors[i % baseColors.length]);
    width.push(3);
    dashArray.push(0);
    dataLabelSeries.push(series.length - 1);
    categoryValues.push((category.data || []).map(Number));
  });

  // 2) Per-category trend lines — dashed, colour-matched. Skipped when the
  //    category has fewer than two sessions (nothing to fit).
  list.forEach((category, i) => {
    const fitted = linearTrend(categoryValues[i]);
    if (!fitted.length) return;
    series.push({ name: `${category.name} (${trendLabel})`, data: fitted });
    colors.push(baseColors[i % baseColors.length]);
    width.push(5);
    dashArray.push(3);
  });

  // 3) Overall trend — regression of the per-session average across categories.
  const overallFitted = linearTrend(overallSeries(categoryValues));
  if (overallFitted.length) {
    series.push({ name: `${overallLabel} (${trendLabel})`, data: overallFitted });
    colors.push(overallColor);
    width.push(6);
    dashArray.push(4);
  }

  return { series, colors, width, dashArray, dataLabelSeries };
}
